import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import { getVehicleDescriptionError } from '../../../utils/validation';
import api from '../../../utils/api';

/**
 * Add/Edit Vehicle Modal Component
 *
 * @param {Boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when vehicle is confirmed with vehicle data
 * @param {Array} units - List of available units
 * @param {Array} vehicleTypes - List of available vehicle types from API
 * @param {Object} vehicleData - Vehicle data for editing (null for adding)
 * @param {Boolean} editMode - Whether in edit mode
 */
function AddVehicleModal({ open, onClose, onConfirm, units = [], vehicleTypes = [], vehicleData = null, editMode = false }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    vehicleTypeId: '',
    licensePlate: '',
    unitId: '',
    identificationInfo: '',
    description: '',
  });
  const [errors, setErrors] = useState({});

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && vehicleData) {
      setFormData({
        vehicleTypeId: vehicleData.vehicleTypeId !== undefined ? vehicleData.vehicleTypeId : '',
        licensePlate: vehicleData.licensePlate || '',
        unitId: vehicleData.unitId !== undefined ? vehicleData.unitId : '',
        identificationInfo: vehicleData.identificationInfo || '',
        description: vehicleData.description || '',
      });
    } else {
      setFormData({
        vehicleTypeId: '',
        licensePlate: '',
        unitId: '',
        identificationInfo: '',
        description: '',
      });
    }
    setErrors({});
  }, [editMode, vehicleData, open]);

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'description') {
      const descriptionError = getVehicleDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    if (field === 'licensePlate') {
      if (value.trim() === '') {
        delete newErrors.licensePlate;
      } else {
        // Validate License Plate format: uppercase letters and numbers, 6-12 characters
        const licensePlatePattern = /^[A-Z0-9]{6,12}$/;
        if (!licensePlatePattern.test(value)) {
          newErrors.licensePlate = t('vehicles.validation.licensePlateInvalidFormat');
        } else {
          delete newErrors.licensePlate;
        }
      }
    }

    if (field === 'identificationInfo') {
      if (value.trim() === '') {
        delete newErrors.identificationInfo;
      } else {
        // Validate RFID format: letters, numbers, and hyphens, 8-32 characters
        const rfidPattern = /^[A-Za-z0-9-]{8,32}$/;
        if (!rfidPattern.test(value)) {
          newErrors.identificationInfo = t('vehicles.validation.identificationInfoInvalidFormat');
        } else {
          delete newErrors.identificationInfo;
        }
      }
    }

    setErrors(newErrors);
  };


  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.vehicleTypeId !== '' && formData.licensePlate.trim() !== '' && formData.unitId !== '';
  };

  // Validate form
  const validateForm = async () => {
    const newErrors = {};

    const descriptionError = getVehicleDescriptionError(formData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    // Validate License Plate format
    if (formData.licensePlate.trim() !== '') {
      const licensePlatePattern = /^[A-Z0-9]{6,12}$/;
      if (!licensePlatePattern.test(formData.licensePlate)) {
        newErrors.licensePlate = t('vehicles.validation.licensePlateInvalidFormat');
      }
    }

    // Validate RFID format (identificationInfo)
    if (formData.identificationInfo.trim() !== '') {
      const rfidPattern = /^[A-Za-z0-9-]{8,32}$/;
      if (!rfidPattern.test(formData.identificationInfo)) {
        newErrors.identificationInfo = t('vehicles.validation.identificationInfoInvalidFormat');
      }
    }

    // Check for duplicate License Plate within the same Unit
    if (formData.licensePlate.trim() !== '' && formData.unitId !== '') {
      try {
        const response = await api.get('/vehicles');
        const existingVehicles = response.items || response;

        // Check if license plate already exists in the same unit (excluding current vehicle in edit mode)
        const isDuplicateLicensePlate = existingVehicles.some(vehicle => {
          const isSameLicensePlate = vehicle.licensePlate === formData.licensePlate;
          const isSameUnit = vehicle.unitId === parseInt(formData.unitId, 10);
          const isDifferentId = editMode ? vehicle.id !== vehicleData?.id : true;
          return isSameLicensePlate && isSameUnit && isDifferentId;
        });

        if (isDuplicateLicensePlate) {
          newErrors.licensePlate = t('vehicles.validation.licensePlateDuplicate');
        }
      } catch (error) {
        console.error('Error checking duplicate license plate:', error);
      }
    }

    // Check for duplicate RFID (identificationInfo) if provided
    if (formData.identificationInfo.trim() !== '') {
      try {
        const response = await api.get('/vehicles');
        const existingVehicles = response.items || response;

        // Check if RFID already exists (excluding current vehicle in edit mode)
        const isDuplicateRFID = existingVehicles.some(vehicle => {
          const isSameRFID = vehicle.identificationInfo === formData.identificationInfo;
          const isDifferentId = editMode ? vehicle.id !== vehicleData?.id : true;
          return isSameRFID && isDifferentId;
        });

        if (isDuplicateRFID) {
          newErrors.identificationInfo = t('vehicles.validation.identificationInfoDuplicate');
        }
      } catch (error) {
        console.error('Error checking duplicate RFID:', error);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    if (editMode && vehicleData) {
      // Include the vehicle ID for update
      onConfirm({ ...formData, id: vehicleData.id });
    } else {
      onConfirm(formData);
    }
    handleClose();
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      vehicleTypeId: '',
      licensePlate: '',
      unitId: '',
      identificationInfo: '',
      description: '',
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('vehicles.editDialog.title') : t('vehicles.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2 }}>
          <Select
            label={t('vehicles.addDialog.vehicleType')}
            value={formData.vehicleTypeId}
            onChange={handleChange('vehicleTypeId')}
            options={vehicleTypes}
            required
            placeholder={t('vehicles.addDialog.vehicleTypePlaceholder')}
            id="vehicleTypeId"
            name="vehicleTypeId"
            data-testid="vehicle-modal-select-vehicleTypeId"
          />
          <Input
            label={t('vehicles.addDialog.licensePlate')}
            value={formData.licensePlate}
            onChange={handleChange('licensePlate')}
            required
            placeholder={t('vehicles.addDialog.licensePlatePlaceholder')}
            error={!!errors.licensePlate}
            helperText={errors.licensePlate || t('vehicles.validation.licensePlateFormatHint')}
            id="licensePlate"
            name="licensePlate"
            data-testid="vehicle-modal-input-licensePlate"
          />
          <Select
            label={t('vehicles.addDialog.unit')}
            value={formData.unitId}
            onChange={handleChange('unitId')}
            options={units}
            required
            emptyMessage={t('vehicles.addDialog.unitPlaceholder')}
            id="unitId"
            name="unitId"
            data-testid="vehicle-modal-select-unitId"
          />
          <Input
            label={t('vehicles.addDialog.identificationInfo')}
            value={formData.identificationInfo}
            onChange={handleChange('identificationInfo')}
            placeholder={t('vehicles.addDialog.identificationInfoPlaceholder')}
            error={!!errors.identificationInfo}
            helperText={errors.identificationInfo || t('vehicles.validation.identificationInfoFormatHint')}
            id="identificationInfo"
            name="identificationInfo"
            data-testid="vehicle-modal-input-identificationInfo"
          />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Input
              label={t('vehicles.addDialog.description')}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={t('vehicles.addDialog.descriptionPlaceholder')}
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description}
              id="description"
              name="description"
              data-testid="vehicle-modal-input-description"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="vehicle-modal-button-cancel"
        >
          {t('vehicles.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="vehicle-modal-button-confirm"
        >
          {editMode ? t('vehicles.addDialog.save') : t('vehicles.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddVehicleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  units: PropTypes.array,
  vehicleTypes: PropTypes.array,
  vehicleData: PropTypes.object,
  editMode: PropTypes.bool,
};

export default AddVehicleModal;
